import { Tag } from 'pleinchamp-api-client';
import React, { FC, useContext, useEffect, useState } from 'react';
import { Text } from '@components/Text/Text.component';
import { Value } from '@form/business/form.types';
import { Checkbox } from '@form/components/Checkbox/Checkbox.component';
import { MultiSelect } from '@form/components/MultiSelect/MultiSelect.component';
import { useTranslation } from '@locales/useTranslation.hook';
import { Breakpoint, useClassNames, useIsBreakpointDown } from '@styles/breakpoints';
import { useDebounce } from '@utils/debounce.hook';
import { InnovationsHomeContext } from 'pages/les-innovations';

import styles from './InnovationsHomeFilters.module.scss';

const MAX_CHARACTERS_SEARCH = 40;

function fromObjectToValueGetter(option: Tag): Value {
  return {
    label: option.label,
    value: option.remoteId,
  };
}

function fromValueToObjectGetter(values: Value[], options: Tag[] | undefined): Tag[] {
  if (!options) {
    return [];
  }
  return options.filter(option =>
    values.some(value => value.value === option.remoteId && value.label === option.label)
  );
}

export const InnovationsHomeFilters: FC = () => {
  const { t } = useTranslation(['innovations']);
  const topBoxFilters = useClassNames({
    all: [styles['innovations-home-filters-desktop'], 'flex'],
    s: ['row'],
  });
  const selectBoxFilters = useClassNames({ all: [styles['innovations-home-filters-desktop'], 'flex'], s: ['row'] });
  const bottomBoxFilters = useClassNames({ all: [styles['innovations-home-filters-desktop'], 'flex', styles['justify-content']] });

  const {
    domainsFilterSelected: { data: domainsFilterSelected, setData: setDomains },
    sectorsFilterSelected: { data: sectorsFilterSelected, setData: setSectors },
    stakesFilterSelected: { data: stakesFilterSelected, setData: setStakes },
    query: { data: query, setData: setQuery },
    offset: { setData: setOffset },
    isHostedByCa: { data: hostedByCa, setData: setHostedByCa },
    domainsList: { data: domainsList },
    stakesList: { data: stakesList },
    sectorsList: { data: sectorsList },
  } = useContext(InnovationsHomeContext);
  const [domainsOptions, setDomainsOptions] = useState<Array<Value>>([]);
  const [sectorsOptions, setSectorsOptions] = useState<Array<Value>>([]);
  const [stakesOptions, setStakesOptions] = useState<Array<Value>>([]);
  const [inputQuery, setInputQuery] = useState('');
  const isMobile = useIsBreakpointDown(Breakpoint.m);

  useEffect(() => {
    setStakesOptions(stakesList ? stakesList.map(stake => fromObjectToValueGetter(stake)) : []);
    setSectorsOptions(sectorsList ? sectorsList.map(sector => fromObjectToValueGetter(sector)) : []);
    setDomainsOptions(domainsList ? domainsList.map(domain => fromObjectToValueGetter(domain)) : []);
  }, [stakesList, sectorsList, domainsList]);

  useEffect(() => {
    if (setOffset) {
      setOffset(0);
    }
  }, [domainsFilterSelected, sectorsFilterSelected, stakesFilterSelected, query, hostedByCa]);

  const queryString = useDebounce(inputQuery, 1000);
  useEffect(() => {
    if (setQuery) {
      setQuery(queryString);
    }
  }, [queryString]);

  const onInputValueChange = (e: React.FormEvent) => {
    const target = e.target as HTMLInputElement;
    setInputQuery(target.value);
  };

  const handleSectorsChange = (value: Value[] | undefined) => {
    if (value && setSectors) {
      setSectors(fromValueToObjectGetter(value, sectorsList));
    }
  };

  const handleDomainsChange = (value: Value[] | undefined) => {
    if (value && setDomains) {
      setDomains(fromValueToObjectGetter(value, domainsList));
    }
  };

  const handleStakesChange = (value: Value[] | undefined) => {
    if (value && setStakes) {
      setStakes(fromValueToObjectGetter(value, stakesList));
    }
  };

  const resetForm = () => {
    if (setStakes && setSectors && setDomains && setHostedByCa && setQuery) {
      setStakes([]);
      setSectors([]);
      setDomains([]);
      setHostedByCa(false);
      setQuery('');
      setInputQuery('');
    }
  };

  return (
    <>
      <div className={` ${topBoxFilters}`}>
        <input
          className={`${styles["inno-input"]} p plc-form-element`}
          inputMode="search"
          maxLength={MAX_CHARACTERS_SEARCH}
          // eslint-disable-next-line react/jsx-no-bind
          onChange={onInputValueChange}
          placeholder={t('innovations.filter.search')}
          type="text"
          value={inputQuery}
        />
        <Checkbox
          checked={hostedByCa}
          className={styles["justify-checkbox"]}
          disabled={false}
          label={
            <Text
              className={styles["align-checkbox"]}
              flavour="dark"
              i18nKey="innovations.filter.heberge.villageca"
              tag="div"
              variant={isMobile ? 'small' : 'p'}
            />
          }
          multilineLabel
          // eslint-disable-next-line react/jsx-no-bind, no-console
          onCheck={checked => setHostedByCa && setHostedByCa(checked)}
        />
      </div>
      <div className={selectBoxFilters}>
        <MultiSelect
          label={
            sectorsFilterSelected && sectorsFilterSelected.length > 0
              ? t('innovations.filter.filieres.selected', { count: sectorsFilterSelected.length })
              : t('innovations.filter.filieres')
          }
          labelFlavour={sectorsFilterSelected && sectorsFilterSelected.length > 0 ? 'green' : 'grey'}
          // eslint-disable-next-line react/jsx-no-bind
          onSelect={handleSectorsChange}
          options={sectorsOptions}
          values={(sectorsFilterSelected || []).map(sectorFilterSelected =>
            fromObjectToValueGetter(sectorFilterSelected)
          )}
        />
        <MultiSelect
          label={
            stakesFilterSelected && stakesFilterSelected.length > 0
              ? t('innovations.filter.enjeux.selected', { count: stakesFilterSelected.length })
              : t('innovations.filter.enjeux')
          }
          labelFlavour={stakesFilterSelected && stakesFilterSelected.length > 0 ? 'green' : 'grey'}
          // eslint-disable-next-line react/jsx-no-bind
          onSelect={handleStakesChange}
          options={stakesOptions}
          values={(stakesFilterSelected || []).map(stakeFilterSelected => fromObjectToValueGetter(stakeFilterSelected))}
        />
        <MultiSelect
          label={
            domainsFilterSelected && domainsFilterSelected.length > 0
              ? t('innovations.filter.domaines.selected', { count: domainsFilterSelected.length })
              : t('innovations.filter.domaines')
          }
          labelFlavour={domainsFilterSelected && domainsFilterSelected.length > 0 ? 'green' : 'grey'}
          // eslint-disable-next-line react/jsx-no-bind
          onSelect={handleDomainsChange}
          options={domainsOptions}
          values={(domainsFilterSelected || []).map(domainFilterSelected =>
            fromObjectToValueGetter(domainFilterSelected)
          )}
        />
      </div>
      <div className={bottomBoxFilters}>
        <Text
          className={styles["underline"]}
          flavour="grey"
          // eslint-disable-next-line react/jsx-no-bind
          onClick={resetForm}
          tag="div"
          variant="small"
        >
          Supprimer mes choix
        </Text>
      </div>
    </>
  );
};
